import dayjs from 'dayjs';
import { dateToString, formatTimezone } from './moment';
import { orangeClockIcon, redClockIcon, yellowClockIcon } from './assets';
import type { DateFormatterProps } from './types';

const DOUBLE_HYPHEN_BULLET = '⁃⁃';

/**
 * Displays a study or report date with an optional facility timezone label and SLA status icon. Destructures the display options from props with defaults, renders the text built by generateDate plus generateTimeZone in a span, and appends the color-coded clock icon from generateSlaClockIcon when hasIcon and a date are both present.
 *
 * @param props - Date values, format string, timezone and facility settings plus the hasIcon and display-mode flags.
 * @returns The rendered date container element.
 */
const DateFormatter = (props: DateFormatterProps) => {
  const {
    startStudyDate = undefined,
    date = undefined,
    anotherDate = undefined,
    format = 'MM/DD/YYYY',
    timezone = undefined,
    facilityCountry = undefined,
    facilityTimezone = undefined,
    hasIcon = false,
    facility = undefined,
    isBreakDayAndTime = false,
    isDayjs = false,
  } = props;

  /**
   * Renders a colored clock icon marking how overdue an SLA date is. Measures the fractional hour difference from now with dayjs and picks the red icon above 24 hours, orange above 12 hours, and yellow otherwise.
   *
   * @param dateValue - The date whose staleness is measured against the current time.
   * @returns The clock icon image element, or null.
   */
  const generateSlaClockIcon = (dateValue: any) => {
    const diff = dayjs().diff(dayjs(dateValue), 'hour', true);
    let clockIcon;
    if (diff > 24) {
      clockIcon = redClockIcon;
    } else if (diff > 12) {
      clockIcon = orangeClockIcon;
    } else {
      clockIcon = yellowClockIcon;
    }
    if (clockIcon) {
      return <img className="clock-icon ml-1" src={clockIcon} alt="Clock icon" />;
    }
    return null;
  };

  /**
   * Produces the display string for a study date across the formatter's several display modes. Returns the double-hyphen placeholder unchanged, formats a Day.js value directly, splits date and time onto two lines when isBreakDayAndTime, joins date and anotherDate with a dash, and otherwise converts through dateToString when a timezone is given or plain dayjs formatting when not.
   *
   * @returns The formatted date string, or an empty string when there is no date.
   */
  const generateDate = () => {
    if (date === DOUBLE_HYPHEN_BULLET) {
      return date;
    }

    if (isDayjs) {
      return date.clone().format(format);
    }

    if (isBreakDayAndTime) {
      if (!format || !date) {
        return '';
      }

      const [dateFormat, timeFormat] = format.split(' ');

      const dateString = dateToString({
        startStudyDate,
        isoDate: date,
        timezone,
        studyTimeOffset: props.studyTimeOffset,
        facilityCountry,
        facilityTimezone,
        format: dateFormat,
      });

      const timeString = dateToString({
        startStudyDate,
        isoDate: date,
        timezone,
        studyTimeOffset: props.studyTimeOffset,
        facilityCountry,
        facilityTimezone,
        format: timeFormat,
      });
      return `${dateString}\n${timeString}`;
    }

    if (anotherDate) {
      const timeArray = [];
      if (date) {
        timeArray.push(
          dateToString({
            startStudyDate,
            isoDate: date,
            timezone,
            studyTimeOffset: props.studyTimeOffset,
            facilityCountry,
            facilityTimezone,
            format,
          }),
        );
      }
      timeArray.push(
        dateToString({
          startStudyDate,
          isoDate: anotherDate,
          timezone,
          studyTimeOffset: props.studyTimeOffset,
          facilityCountry,
          facilityTimezone,
          format,
        }),
      );
      return timeArray.join(' - ');
    }

    if (date) {
      if (timezone) {
        return dateToString({
          startStudyDate,
          isoDate: date,
          timezone,
          studyTimeOffset: props.studyTimeOffset,
          facilityCountry,
          facilityTimezone,
          format,
        });
      }

      return dayjs(date).format(format);
    }

    return '';
  };

  /**
   * Builds the timezone suffix shown beside a formatted study date. Calls formatTimezone with the start study date, ISO date, timezone and facility when a facility is present, and returns an empty string otherwise.
   *
   * @returns The timezone label, or an empty string when no facility is given.
   */
  const generateTimeZone = () => {
    if (facility) {
      return formatTimezone({
        startStudyDate,
        isoDate: date,
        timezone,
        facility,
      });
    }
    return '';
  };

  /**
   * Composes the full date text rendered by the formatter. Joins the formatted date from generateDate with the facility timezone label from generateTimeZone using a single space.
   *
   * @returns The date text followed by its timezone label.
   */
  const renderTime = () => {
    const time = [generateDate(), generateTimeZone()];
    return time.join(' ');
  };

  return (
    <div className="date-container">
      <span>{renderTime()}</span>

      {hasIcon && !!date && generateSlaClockIcon(date)}
    </div>
  );
};

export default DateFormatter;
